import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CustomPrintWizard from './pages/CustomPrintWizard';
import ArtGallery from './pages/ArtGallery';
import Shop from './pages/Shop';
import ArtistShop from './pages/ArtistShop';
import ArtworkDetail from './pages/ArtworkDetail';
import Cart from './pages/Cart';
import PaymentCheckout from './pages/PaymentCheckout';
import PurchaseHistory from './pages/PurchaseHistory';
import Addresses from './pages/Addresses';
import Returns from './pages/Returns';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import AdminProducts from './pages/admin/AdminProducts';
import AdminShipping from './pages/admin/AdminShipping';
import AdminAspectRatios from './pages/admin/AdminAspectRatios';
import AdminSizes from './pages/admin/AdminSizes';
import AdminTags from './pages/admin/AdminTags';
import AdminPromotions from './pages/admin/AdminPromotions';
import AdminReturns from './pages/admin/AdminReturns';
import AdminStretcherBars from './pages/admin/AdminStretcherBars';
import AdminMaterialPricing from './pages/admin/AdminMaterialPricing';
import AdminArtists from './pages/admin/AdminArtists';
import AdminArtworks from './pages/admin/AdminArtworks';
import AdminArtistApplications from './pages/admin/AdminArtistApplications';
import Profile from './pages/Profile';
import BecomeAnArtist from './pages/BecomeAnArtist';
import ArtistMetrics from './pages/ArtistMetrics';
import ArtistSales from './pages/ArtistSales';
import AdminMetrics from './pages/admin/AdminMetrics';
import ArtistProfile from './pages/ArtistProfile';
import AdminSettings from './pages/admin/AdminSettings';
import ArtistStorefront from './pages/ArtistStorefront';
import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import CookiePolicy from './pages/legal/CookiePolicy';
import CopyrightPolicy from './pages/legal/CopyrightPolicy';
import ReturnsPolicy from './pages/legal/ReturnsPolicy';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import CookieConsent from './components/CookieConsent';
import Footer from './components/Footer';
import RouteChangeHandler from './components/RouteChangeHandler';
import './App.css';

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <div className="App">
            <RouteChangeHandler />
            <Navbar />
            <CookieConsent />
            <main className="main-content" id="main-content">
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Legacy path: the wizard replaced the old upload page */}
              <Route path="/upload" element={<Navigate to="/create-print" replace />} />
              <Route path="/create-print" element={<CustomPrintWizard />} />
                  <Route path="/art" element={<Shop />} />
                  <Route path="/art/browse" element={<ArtGallery />} />
                  <Route path="/artists/:artistId/shop" element={<ArtistShop />} />
                  <Route path="/artworks/:artworkId" element={<ArtworkDetail />} />
                  <Route path="/cart" element={<Cart />} />
              <Route path="/payment/success" element={<PaymentCheckout />} />
              <Route path="/payment/cancel" element={<PaymentCheckout />} />
              <Route path="/history" element={<PrivateRoute><PurchaseHistory /></PrivateRoute>} />
              <Route path="/addresses" element={<PrivateRoute><Addresses /></PrivateRoute>} />
              <Route path="/returns" element={<PrivateRoute><Returns /></PrivateRoute>} />
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
              <Route path="/admin/artworks" element={<AdminRoute><AdminArtworks /></AdminRoute>} />
              <Route path="/artist/artworks" element={<PrivateRoute><AdminArtworks /></PrivateRoute>} />
              <Route path="/admin/sizes" element={<AdminRoute><AdminSizes /></AdminRoute>} />
              <Route path="/admin/aspect-ratios" element={<AdminRoute><AdminAspectRatios /></AdminRoute>} />
              <Route path="/admin/shipping" element={<AdminRoute><AdminShipping /></AdminRoute>} />
              <Route path="/admin/material-pricing" element={<AdminRoute><AdminMaterialPricing /></AdminRoute>} />
              <Route path="/admin/artists" element={<AdminRoute><AdminArtists /></AdminRoute>} />
              <Route path="/admin/artist-applications" element={<AdminRoute><AdminArtistApplications /></AdminRoute>} />
              <Route path="/admin/metrics" element={<AdminRoute><AdminMetrics /></AdminRoute>} />
              <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
              <Route path="/admin/tags" element={<AdminRoute><AdminTags /></AdminRoute>} />
                  <Route path="/admin/promotions" element={<AdminRoute><AdminPromotions /></AdminRoute>} />
                  <Route path="/admin/coupons" element={<AdminRoute><AdminPromotions /></AdminRoute>} />
                  <Route path="/admin/sales" element={<AdminRoute><AdminPromotions /></AdminRoute>} />
                  <Route path="/admin/returns" element={<AdminRoute><AdminReturns /></AdminRoute>} />
              <Route path="/admin/stretcher-bars" element={<AdminRoute><AdminStretcherBars /></AdminRoute>} />
                  <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                  <Route path="/become-an-artist" element={<BecomeAnArtist />} />
                  <Route path="/artist/metrics" element={<PrivateRoute><ArtistMetrics /></PrivateRoute>} />
                  <Route path="/artist/sales" element={<PrivateRoute><ArtistSales /></PrivateRoute>} />
                  <Route path="/artist/storefront" element={<PrivateRoute><ArtistStorefront /></PrivateRoute>} />
              <Route path="/artists/:artistId" element={<ArtistProfile />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/copyright" element={<CopyrightPolicy />} />
              <Route path="/returns-policy" element={<ReturnsPolicy />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;

